import { FormEvent, useState } from 'react';
import { DayOfWeek, type User, type FamilyRole } from '@kinapp/shared';
import { api } from '../lib/api';
import { FormField } from './FormField';

interface CreateMedicationFormProps {
  familyId: string;
  dependentId: string;
  familyMembers: Array<{ user: User; role: FamilyRole }>;
  onSuccess: () => void;
  onCancel: () => void;
}

const DAYS_OF_WEEK: DayOfWeek[] = [
  DayOfWeek.MONDAY,
  DayOfWeek.TUESDAY,
  DayOfWeek.WEDNESDAY,
  DayOfWeek.THURSDAY,
  DayOfWeek.FRIDAY,
  DayOfWeek.SATURDAY,
  DayOfWeek.SUNDAY,
];

export function CreateMedicationForm({
  familyId,
  dependentId,
  familyMembers,
  onSuccess,
  onCancel,
}: CreateMedicationFormProps) {
  const [name, setName] = useState('');
  const [dose, setDose] = useState('');
  const [timesPerDay, setTimesPerDay] = useState(1);
  const [selectedDays, setSelectedDays] = useState<DayOfWeek[]>([...DAYS_OF_WEEK]);
  const [assignedToId, setAssignedToId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const toggleDay = (day: DayOfWeek) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Medication name is required');
      return;
    }

    if (timesPerDay < 1) {
      setError('Times per day must be at least 1');
      return;
    }

    if (selectedDays.length === 0) {
      setError('Select at least one day');
      return;
    }

    setLoading(true);

    try {
      await api.createMedication({
        familyId,
        dependentId,
        name: name.trim(),
        dose: dose.trim() || undefined,
        timesPerDay,
        daysOfWeek: selectedDays,
        assignedToId: assignedToId || undefined,
      });

      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to create medication');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && (
        <div className="rounded-md bg-red-50 p-4 mb-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <FormField label="Medication name" id="medication-name" required>
        <input
          type="text"
          id="medication-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="input"
          placeholder="e.g., Aspirin"
          required
        />
      </FormField>

      <FormField label="Dose" id="medication-dose">
        <input
          type="text"
          id="medication-dose"
          value={dose}
          onChange={(e) => setDose(e.target.value)}
          className="input"
          placeholder="e.g., 10mg"
        />
      </FormField>

      <FormField label="Times per day" id="medication-times" required>
        <input
          type="number"
          id="medication-times"
          value={timesPerDay}
          onChange={(e) => setTimesPerDay(Number(e.target.value))}
          className="input"
          min="1"
          required
        />
      </FormField>

      <FormField label="Days of week" id="medication-days" required>
        <div className="grid grid-cols-7 gap-2">
          {DAYS_OF_WEEK.map((day) => (
            <button
              key={day}
              type="button"
              onClick={() => toggleDay(day)}
              className={`p-2 text-xs rounded transition-colors ${
                selectedDays.includes(day)
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {day.slice(0, 3)}
            </button>
          ))}
        </div>
      </FormField>

      <FormField label="Assigned to" id="medication-assigned">
        <select
          id="medication-assigned"
          value={assignedToId}
          onChange={(e) => setAssignedToId(e.target.value)}
          className="input"
        >
          <option value="">Unassigned</option>
          {familyMembers.map(({ user }) => (
            <option key={user.id} value={user.id}>
              {user.name}
            </option>
          ))}
        </select>
      </FormField>

      <div className="flex gap-3 mt-8">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 btn btn-secondary"
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="flex-1 btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={loading}
        >
          {loading ? 'Creating...' : 'Create medication'}
        </button>
      </div>
    </form>
  );
}
